import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, takeUntil } from 'rxjs';

import { BadgingService } from 'app/modules/social/services/badging/badging.service';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeQuestion from 'entity/BadgeQuestion';
import RegistrationField from 'entity/RegistrationField';
import SocialEvent from 'entity/SocialEvent';
import QuestionVisibility from 'enums/QuestionVisibility';
import RegistrationStatus from 'enums/RegistrationStatus';

import { ModalAddFieldsComponent } from '../../components/modal-add-fields/modal-add-fields.component';
import { EventsService } from '../../services/events.service';

const TOOLTIP_TEXTS = {
  details: 'To edit your event details, go to the details page.',
  registration:
    'These are the fields that will display on your registration page. Remove, add, or mark them as required fields.',
  status:
    'Form Status displays the status of your form on the internet. Pause or resume your registration page at any time.',
  embed: 'Embed this form on your website to collect responses directly from your visitors.',
  preview: 'This is a preview of what the registration form will look like.',
};

interface FormStatusChip {
  title: string;
  background: string;
  color: string;
}

interface RegistrationFieldInfo extends RegistrationField {
  disabled?: boolean;
}

export enum AccountSettingsFields {
  EMAIL = 'Email',
  FIRST_NAME = 'First Name',
  LAST_NAME = 'Last Name',
  PROFILE_PICTURE = 'Profile Picture',
}

// these fields are derived from account settings rather than badge questions
export const defaultRegistrationFields: RegistrationField[] = [
  { name: AccountSettingsFields.EMAIL, description: '', required: true },
  { name: AccountSettingsFields.FIRST_NAME, description: '', required: true },
  { name: AccountSettingsFields.LAST_NAME, description: '', required: true },
  { name: AccountSettingsFields.PROFILE_PICTURE, description: '', required: false },
];
export const requiredRegistrationFieldsNames = defaultRegistrationFields.map((field) =>
  field.required ? (field.name?.toLowerCase() ?? '') : '',
);
export const defaultRegistrationFieldsNames = defaultRegistrationFields.flatMap(
  (field) => field.name?.toLowerCase() ?? '',
);

@Component({
  selector: 'app-registration-form-page',
  templateUrl: './registration-form-page.component.html',
  styleUrl: './registration-form-page.component.scss',
  standalone: false,
})
export class RegistrationFormPageComponent extends Loggable implements OnDestroy, OnInit {
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected event: SocialEvent = {};
  protected badgeQuestions: BadgeQuestion[] = [];
  protected registrationFields: RegistrationFieldInfo[] = [];
  protected tooltips = TOOLTIP_TEXTS;

  protected loading = false;
  protected smartBadgeLink: string[] = [];

  public constructor(
    private store: Store,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private eventsService: EventsService,
    private _toasterService: ToastrService,
    private badgingService: BadgingService,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.getSelectedEvent();
  }

  private getSelectedEvent() {
    this.loading = true;
    const eventId = this.route.snapshot.paramMap.get('id');
    if (!eventId) {
      return;
    }

    this.eventsService
      .getEvent(eventId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (event) => {
        this.event = event;
        this.smartBadgeLink = ['/home/scootaverses/', event.socialRoomSetId ?? '', 'smart-badging'];
        this.badgeQuestions = (await this.badgingService.getBadgeQuestions(event.socialRoomSetId ?? '')).filter(
          (badgeQuestion) => {
            return (
              // TODO: is this correct?
              badgeQuestion.visibility === QuestionVisibility.ALL
            );
          },
        );
        // set disabled if appears in required fields

        const registrationFields = await this.eventsService.getFormRegistrationField(event);

        if (registrationFields?.length) {
          this.registrationFields = registrationFields.map((field) => {
            return {
              ...field,
              disabled: requiredRegistrationFieldsNames.includes(field.name?.toLowerCase() ?? ''),
            };
          });
        }
        this.loading = false;
      });
  }

  protected drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.registrationFields, event.previousIndex, event.currentIndex);
    this.saveRegistrationForm().catch((err) => this.error(err));
  }

  protected changeField() {
    this.saveRegistrationForm().catch((err) => this.error(err));
  }

  protected removeField(index: number) {
    this.registrationFields.splice(index, 1);
    this.saveRegistrationForm().catch((err) => this.error(err));
  }

  protected openOptionsDialog() {
    const dialogRef = this.dialog.open(ModalAddFieldsComponent, {
      data: { registrationFields: this.registrationFields, badgeQuestions: this.badgeQuestions },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: BadgeQuestion[] | undefined) => {
        if (result?.length) {
          result.forEach((field) => {
            this.registrationFields.push(field);
          });

          this.saveRegistrationForm()
            .then(() => this._toasterService.success('Registration Form saved successfully!'))
            .catch((err) => this.error(err));
        }
      });
  }

  private async saveRegistrationForm() {
    if (!this.event.socialEventId) {
      this.error('socialEventId is undefined');
      return;
    }

    const mappedFields = this.registrationFields.map((field) => {
      return {
        name: field.name,
        description: field.description,
        required: field.required,
      };
    });

    await this.eventsService
      .saveRegistrationFields(this.event.socialEventId, mappedFields)
      .catch((err) => this.error(err));
  }

  protected async changeRegistrationStatus() {
    const newEvent = { ...this.event };

    if (newEvent.registrationStatus === RegistrationStatus.NOT_CREATED) {
      await this.saveRegistrationForm();
    }

    newEvent.registrationStatus =
      newEvent.registrationStatus === RegistrationStatus.LIVE ? RegistrationStatus.PAUSED : RegistrationStatus.LIVE;

    this.eventsService
      .updateEvent(newEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this._toasterService.success('Status successfully updated!');
          this.event = newEvent;
        },
        error: (error: unknown) => {
          this._toasterService.error('Error updating status, try again later.');
          this.error(String(error));
        },
      });
  }

  protected copyRegistrationLink() {
    if (!this.event.eventLink) {
      this.error('No event link received');
      return;
    }

    const parts = this.event.eventLink.split('/');
    parts.splice(-3, 0, 'web');
    const regUrl = parts.join('/');

    const text = `${regUrl}/registration`;

    navigator.clipboard
      .writeText(text)
      .then(() => this._toasterService.success('Copied to clipboard', 'Success'))
      .catch((err) => this.error(err));
  }

  protected copyEmbedCode() {
    if (!this.event.eventLink) {
      this.error('No event link received');
      return;
    }

    const parts = this.event.eventLink.split('/');
    parts.splice(-3, 0, 'web');
    const regUrl = parts.join('/');

    const uri = `${regUrl}/registration?embed=true`;

    const embedCode = `<iframe style="box-shadow: none; border: none;" src="${uri}" width="100%" height="700px"></iframe>`;

    navigator.clipboard
      .writeText(embedCode)
      .then(() => this._toasterService.success('Copied to clipboard', 'Success'))
      .catch((err) => this.error(err));
  }

  protected get formStatus(): FormStatusChip | undefined {
    const status = this.event.registrationStatus ?? 0;

    const formStatusChip: Record<number, FormStatusChip | undefined> = {
      [RegistrationStatus.LIVE]: { title: 'Live', background: '#39AD6533', color: '#003C10' },
      [RegistrationStatus.PAUSED]: { title: 'Paused', background: '#FCF0D9', color: '#A78234' },
      [RegistrationStatus.NOT_CREATED]: { title: 'Not Created', background: '#F2F2F2', color: '#333333' },
      [RegistrationStatus.CLOSED]: { title: 'Closed', background: '#FECACA', color: '#DC2626' },
    };

    if (formStatusChip[status]) {
      return formStatusChip[status];
    }

    return { title: 'Closed', background: '#FECACA', color: '#DC2626' };
  }

  protected get buttonProperty() {
    const status = this.event.registrationStatus ?? 0;

    const buttonProps: Record<number, { class: string; text: string }> = {
      [RegistrationStatus.NOT_CREATED]: { class: 'button-primary', text: 'Launch' },
      [RegistrationStatus.LIVE]: { class: 'button-secondary', text: 'Pause' },
    };

    if (buttonProps[status]) {
      return buttonProps[status];
    }

    return {
      class: 'button-secondary',
      text: 'Launch',
    };
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public get isFormLive(): boolean {
    return this.event.registrationStatus === RegistrationStatus.LIVE;
  }
}
